import React from 'react';
import { FileText, BookOpen, FolderOpen } from 'lucide-react';
import { ChatMessage } from '../../../services/api';
import MarkdownMessage from '../../../components/MarkdownMessage';
import { Spinner } from '@/components/ui/spinner';
import { useDelayedTrue } from '../../../hooks/useDelayedTrue';

const SESSION_LOADING_DELAY_MS = 1000;

interface ChatMessagesProps {
  messages: ChatMessage[];
  loading: boolean;
  sessionLoading?: boolean;
  sessionLoadKey?: string | null;
  streamingMessageId: string | null;
  // Backend SSE phase: 'retrieving' | 'generating' | null. When set, the
  // spinner label shows what the backend is doing instead of the
  // generic "Thinking…". Cleared when the first text token arrives.
  streamStatus?: string | null;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  maxWidth: number;
}

const STATUS_LABELS: Record<string, string> = {
  retrieving: 'Searching sources…',
  generating: 'Generating answer…',
  // Web-search fallback: the library had no sufficiently relevant sources, so
  // the backend is running a domain-restricted web search before answering.
  // Server-side search runs before the first token, so this phase can last
  // tens of seconds — the explicit label prevents an unexplained pause.
  searching_web: 'Searching trusted web sources…',
};

/** Resolve a stream status key to a user-facing label.
 * `searching_web:<query>` is the dynamic form — the backend streams the
 * actual search query as each search executes, so the long search phase
 * shows motion instead of one static label. */
function statusLabel(status: string): string {
  if (status.startsWith('searching_web:')) {
    const query = status.slice('searching_web:'.length).trim();
    if (query) return `Searching the web: “${query}”…`;
  }
  return STATUS_LABELS[status] ?? 'Thinking…';
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  loading,
  sessionLoading = false,
  sessionLoadKey = null,
  streamingMessageId,
  streamStatus,
  messagesEndRef,
  maxWidth,
}) => {
  const showSessionLoading = useDelayedTrue(
    sessionLoading,
    SESSION_LOADING_DELAY_MS,
    sessionLoadKey,
  );

  return (
  <div className="flex-1 overflow-y-auto flex justify-center py-6 px-4 chat-scroll">
    <div className="w-full pb-6" style={{ maxWidth }}>
      {messages.length === 0 ? (
        sessionLoading && showSessionLoading ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center gap-3">
            <Spinner className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading chat…</p>
          </div>
        ) : sessionLoading ? null : (
        <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
          <h2 className="text-3xl font-semibold text-foreground mb-2">
            What would you like to explore?
          </h2>
        </div>
        )
      ) : (
        <div className="flex flex-col gap-6">
          {messages.map((message) => {
            const isUser = message.role === 'user';
            return (
              <div
                key={message.id}
                className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
              >
                {/* Context pills — shown above user messages when context was selected */}
                {isUser && message.context_items && message.context_items.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-1 max-w-[85%] justify-end">
                    {message.context_items.map((item) => {
                      const isUpload = item.context_type === 'upload';
                      const isProject = item.context_type === 'project';
                      const label = isUpload
                        ? (item.file_name || 'Attachment')
                        : isProject
                        ? 'Project'
                        : 'Document';
                      const Icon = isUpload ? FileText : isProject ? FolderOpen : BookOpen;
                      return (
                        <span
                          key={item.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary/20 text-primary-foreground/80 border border-primary/30"
                        >
                          <Icon className="h-3 w-3 shrink-0" />
                          <span className="max-w-[160px] truncate">{label}</span>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div
                  className={
                    isUser
                      ? 'max-w-[85%] min-w-[60px] px-4 py-3 rounded-2xl bg-primary text-primary-foreground'
                      : 'w-full'
                  }
                >
                  <div
                    className={
                      isUser
                        ? 'relative leading-relaxed'
                        : 'relative leading-relaxed text-foreground'
                    }
                  >
                    <MarkdownMessage
                      content={message.content}
                      isUser={isUser}
                      citations={!isUser ? message.citations ?? undefined : undefined}
                    />
                    {streamingMessageId === message.id && (
                      <span className="inline-block w-0.5 h-[1em] ml-1 bg-current animate-pulse" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {loading && !streamingMessageId && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 text-muted-foreground px-4 py-3">
                <Spinner className="h-5 w-5" />
                <span className="text-sm font-medium">
                  {streamStatus ? statusLabel(streamStatus) : 'Thinking…'}
                </span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      )}
    </div>
  </div>
  );
};

